'use client';

import { cn } from '@/lib/ui/cn';
import { useEqualHeight } from '@/hooks/useEqualHeight';
import { BlockDefinitionListProps } from '@/types/blocks';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

export function DefinitionList({
  heading,
  subheading,
  list = [],
  stretch = false,
}: BlockDefinitionListProps) {
  const [toggle, setToggle] = useState(false);
  const t = useTranslations('ListAside.toggle');

  const toggleList = () => setToggle(prev => !prev);

  const single = stretch && list.length === 1;

  const headingRef = useEqualHeight<HTMLParagraphElement>('heading', single);
  const subheadingRef = useEqualHeight<HTMLParagraphElement>(
    'subheading',
    single
  );
  const itemHeadingRef = useEqualHeight<HTMLParagraphElement>(
    'item-heading',
    single
  );

  return (
    <div
      data-equalize-root
      className={cn(
        'h-full w-full rounded-4xl bg-white p-7.5 md:py-12.5',
        single && 'md:flex md:flex-col'
      )}
    >
      <div className='mb-5 space-y-5 md:mb-12 md:space-y-10'>
        {heading && (
          <p
            ref={headingRef}
            className={cn('sh-heading-3 mb-5', single && 'line-clamp-3')}
          >
            {heading}
          </p>
        )}
        {subheading && (
          <p
            ref={subheadingRef}
            className={cn('sh-body', single && 'line-clamp-3')}
          >
            {subheading}
          </p>
        )}
      </div>
      {list.length > 0 && (
        <>
          <div
            className={cn(
              `${toggle ? 'block' : 'hidden'} list-none gap-5 space-y-5 md:space-y-6`,
              single ? 'md:flex md:flex-1 md:flex-col' : 'md:block'
            )}
          >
            {list?.map((item, i) => (
              <li
                key={i}
                className={cn(
                  'bg-sh-cyan rounded-4xl p-7.5',
                  single && 'md:flex-1'
                )}
              >
                {item.heading && (
                  <p
                    ref={single ? itemHeadingRef : undefined}
                    className={cn(
                      'sh-body mb-5 font-bold',
                      single && 'line-clamp-3'
                    )}
                  >
                    {item.heading}
                  </p>
                )}
                {item.description && (
                  <p className='sh-body'>{item.description}</p>
                )}
              </li>
            ))}
          </div>
          <button
            className='md:mt-none text-sh-body mt-5 block font-bold underline md:hidden'
            onClick={toggleList}
          >
            {toggle ? t('close') : t('open')}
          </button>
        </>
      )}
    </div>
  );
}
